/**
 * File: apps/qbench-cp/reactapp/src/shared/utils/string.js
 *
 * Purpose: String-casing helpers: capitalization, camelCase/title-case conversion,
 *     pluralization, object key camelization, and UUID v4 validation.
 *
 * Scope: Generic string utilities used across the customer portal for display formatting
 *     and API payload key normalization.
 *
 * Tags: customer-portal
 *
 * Overview: `ucfirst` capitalizes a string's first character. `toCamelCase` converts a
 *     sentence-style string to camelCase; `toTitleCase` capitalizes each word.
 *     `camelToSentenceCase` inserts spaces before capitalized segments of a camelCase/
 *     PascalCase string. `camelize` recursively converts an object's (or array of objects')
 *     keys to camelCase via `mapKeysDeep`. `pluralize` applies an English pluralization
 *     suffix based on a word's last character (x/s/ch/sh -> "es", o -> "ves", y -> "ies",
 *     default -> "s"). `isValidUUID4` tests a string against a UUID v4 regex pattern.
 *
 * Exports: ucfirst, toCamelCase, toTitleCase, camelToSentenceCase, camelize, pluralize,
 *     isValidUUID4
 */

import camelCase from 'lodash/camelCase';
import { mapKeysDeep } from './misc';

export function ucfirst(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function toCamelCase(str) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '');
}

export function toTitleCase(str) {
  return (str || '').replace(/\w\S*/g, ucfirst);
}

export function camelToSentenceCase(str) {
  return (str || '')
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/([A-Z][a-z])/g, ' $1');
}

/**
 * @param {Object.<string, any>} obj
 */
export function camelize(obj) {
  const fn = (_, k) => camelCase(k);
  return Array.isArray(obj)
    ? obj.map((v) => mapKeysDeep(v, fn))
    : mapKeysDeep(obj, fn);
}

export function pluralize(str) {
  const word = str.toLowerCase();
  const rules = {
    x: (w) => w + 'es',
    s: (w) => w + 'es',
    ch: (w) => w + 'es',
    sh: (w) => w + 'es',
    o: (w) => w.slice(0, -1) + 'ves',
    y: (w) => w.slice(0, -1) + 'ies',
  };
  const lastChar = word.at(-1);
  return rules[lastChar]?.(word) || `${word}s`;
}

export function isValidUUID4(uuid) {
  const uuid4Pattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
  return uuid4Pattern.test(uuid);
}
